import { computed, ref, watch } from '@vue/composition-api';
import { linkMapping } from './util/linkMapping';
import { getOptions } from './util/getOptions';
import {
  subWeeks,
  format,
  isWithinInterval,
  addDays,
  minTime,
  maxTime,
} from 'date-fns';

const LINKS_PER_PAGE = 5;
const DATE_FORMAT = 'yyyy-MM-dd';
const now = Date.now();
const DEFAULT_FROM = format(subWeeks(now, 4), DATE_FORMAT);
const DEFAULT_TO = format(now, DATE_FORMAT);

export const useFilters = ({ links, userId }) => {
  const filteredLinks = ref(links);
  const from = ref(DEFAULT_FROM);
  const to = ref(DEFAULT_TO);
  const myLinks = ref(false);
  const page = ref(0);
  const filterNames = {
    campaign: 'Campaign',
    channel: 'Channel',
    medium: 'Medium',
    source: 'Source',
    marketer: 'Marketer',
  };
  const filterOptions = ref(getOptions(links));
  const filters = [
    { name: 'campaign', selected: ref([]) },
    { name: 'channel', selected: ref([]) },
    { name: 'medium', selected: ref([]) },
    { name: 'source', selected: ref([]) },
    { name: 'marketer', selected: ref([]) },
  ];

  const visibleFilters = computed(() =>
    filters.filter((f) => filterOptions.value[f.name].length !== 0)
  );

  const visibleLinks = computed(() => {
    const startIndex = page.value * LINKS_PER_PAGE;
    return filteredLinks.value.slice(startIndex, startIndex + LINKS_PER_PAGE);
  });
  const pageCount = computed(() =>
    Math.ceil(filteredLinks.value.length / LINKS_PER_PAGE)
  );

  const updateFilterOptions = () => {
    filterOptions.value = getOptions(filteredLinks.value);
  };

  const passesSelectedFilters = (link) =>
    filters.every((f) => {
      if (f.selected.value.length === 0) {
        return true;
      }
      const linkValue = linkMapping[f.name](link);
      return f.selected.value.includes(linkValue);
    });

  const filterAllLinks = () => {
    const range = {
      start: from.value ? new Date(from.value) : minTime,
      end: to.value ? addDays(new Date(to.value), 1) : maxTime,
    };
    filteredLinks.value = links.filter((link) => {
      if (myLinks.value && link.creator !== userId) {
        return false;
      }
      if (!isWithinInterval(new Date(link.created_at), range)) {
        return false;
      }
      return passesSelectedFilters(link);
    });
    updateFilterOptions();
  };

  watch(from, filterAllLinks);
  watch(to, filterAllLinks);
  watch(myLinks, filterAllLinks);

  const updateFilter = (name, selected) => {
    const filter = filters.find((f) => f.name === name);
    const furtherFiltering = filter.selected.value.length < selected.length;
    filter.selected.value = selected;

    if (furtherFiltering) {
      filteredLinks.value = filteredLinks.value.filter((link) => {
        const linkValue = linkMapping[name](link);
        return selected.includes(linkValue);
      });
      updateFilterOptions();
    } else {
      filterAllLinks();
    }
  };
  const clearFilter = (name) => {
    const filter = filters.find((f) => f.name === name);
    filter.selected.value = [];
    filterAllLinks();
  };
  const clearFilters = () => {
    from.value = DEFAULT_FROM;
    to.value = DEFAULT_TO;
    filters.forEach((filter) => {
      filter.selected.value = [];
    });
    filterAllLinks();
  };

  return {
    visibleLinks,
    page,
    pageCount,
    filters: {
      to,
      from,
      myLinks,
      otherFilters: visibleFilters,
    },
    filterOptions,
    filterNames,
    updateFilter,
    clearFilter,
    clearFilters,
  };
};
